(() => {
  if (window.AlawooderEditorial) { window.AlawooderEditorial.mountAll(); return; }
  const instances = new Map();
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pad = value => String(value).padStart(2, '0');
  function init(root) {
    if (!root || root.dataset.awEditorialReady) return;
    root.dataset.awEditorialReady = 'true';
    const cleanups = [];
    const controller = new AbortController();
    const on = (target,type,handler,options = {}) => target.addEventListener(type,handler,{...options,signal:controller.signal});
    root.querySelectorAll('[data-aw-ed-specifications]').forEach(details => {
      const phone = matchMedia('(max-width: 700px)');
      const setMode = () => { details.open = !phone.matches; };
      setMode();
      phone.addEventListener('change', setMode);
      cleanups.push(() => phone.removeEventListener('change', setMode));
    });
    root.querySelectorAll('[data-aw-ed-carousel]').forEach(carousel => {
      const track = carousel.querySelector('[data-aw-ed-track]');
      if (!track) return;
      const cards = [...track.querySelectorAll('[data-aw-ed-card]')];
      const tools = carousel.querySelector('[data-aw-ed-tools]');
      const count = carousel.querySelector('[data-aw-ed-count]');
      const previous = carousel.querySelector('[data-aw-ed-prev]');
      const next = carousel.querySelector('[data-aw-ed-next]');
      if (!cards.length || !tools || !count || !previous || !next) return;
      let active = 0;
      let frame = 0;
      let pending = null;
      let trackWidth = track.clientWidth;
      const positions = () => {
        const start = track.getBoundingClientRect().left;
        return cards.map(card => card.getBoundingClientRect().left - start + track.scrollLeft);
      };
      const update = () => {
        frame = 0;
        const overflow = track.scrollWidth > track.clientWidth + 2;
        tools.hidden = !overflow;
        if (!overflow) { active = 0; pending = null; return; }
        if (pending && Math.abs(track.scrollLeft - pending.left) <= 2) pending = null;
        if (pending) active = pending.index;
        else {
          const offsets = positions();
          active = offsets.reduce((closest, offset, index) => Math.abs(offset - track.scrollLeft) < Math.abs(offsets[closest] - track.scrollLeft) ? index : closest, 0);
          // The final card can stop before its own leading edge when it is fully visible.
          if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) active = cards.length - 1;
        }
        count.textContent = `${pad(active + 1)} / ${pad(cards.length)}`;
        previous.disabled = pending ? active === 0 : track.scrollLeft <= 2;
        next.disabled = pending ? active === cards.length - 1 : track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      const navigate = (index,forceImmediate = false) => {
        cancelAnimationFrame(frame);
        frame = 0;
        const destination = Math.max(0, Math.min(cards.length - 1, index));
        const left = Math.max(0, Math.min(positions()[destination], track.scrollWidth - track.clientWidth));
        const immediate = forceImmediate || reducedMotion();
        pending = !immediate && Math.abs(track.scrollLeft - left) > 2 ? {index: destination, left} : null;
        active = destination;
        track.scrollTo({left, behavior: immediate ? 'instant' : 'smooth'});
        update();
      };
      const move = delta => navigate((pending?.index ?? active) + delta);
      const scheduleUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };
      on(track, 'scroll', scheduleUpdate, {passive:true});
      on(track, 'scrollend', () => {
        if (pending) {
          const destination = pending;
          pending = null;
          track.scrollTo({left: destination.left, behavior: 'instant'});
        }
        scheduleUpdate();
      });
      const interrupt = () => {
        if (!pending) return;
        pending = null;
        track.scrollTo({left: track.scrollLeft, behavior: 'instant'});
        update();
      };
      on(track, 'pointerdown', interrupt, {passive:true});
      on(track, 'wheel', interrupt, {passive:true});
      on(previous, 'click', () => move(-1));
      on(next, 'click', () => move(1));
      on(track, 'keydown', event => {
        if (event.target !== track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          navigate(event.key === 'Home' ? 0 : cards.length - 1);
        } else move(event.key === 'ArrowRight' ? 1 : -1);
      });
      on(track,'aw:pdp:select',event => navigate(event.detail.index,true));
      const observer = new ResizeObserver(() => {
        if (track.clientWidth !== trackWidth) {
          trackWidth = track.clientWidth;
          interrupt();
        }
        scheduleUpdate();
      });
      observer.observe(track);
      update();
      cleanups.push(() => { observer.disconnect(); cancelAnimationFrame(frame); });
    });
    root.querySelectorAll('[data-aw-ed-scene]').forEach(scene => {
      const buttons = [...scene.querySelectorAll('[data-aw-ed-hotspot]')];
      const panels = [...scene.querySelectorAll('[data-aw-ed-panel]')];
      const choose = button => {
        buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        panels.forEach(panel => panel.hidden = panel.dataset.awEdPanel !== button.dataset.awEdHotspot);
      };
      if (buttons[0]) choose(buttons.find(button => button.getAttribute('aria-pressed') === 'true') || buttons[0]);
      buttons.forEach((button, index) => {
        on(button, 'click', () => choose(button));
        on(button, 'keydown', event => {
          if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) return;
          event.preventDefault();
          let destination = index;
          if (event.key === 'Home') destination = 0;
          else if (event.key === 'End') destination = buttons.length - 1;
          else destination = (index + (['ArrowRight','ArrowDown'].includes(event.key) ? 1 : -1) + buttons.length) % buttons.length;
          buttons[destination].focus();
          choose(buttons[destination]);
        });
      });
    });
    on(document,'shopify:block:select',event => {
      if (!root.contains(event.target)) return;
      const card = event.target.closest?.('[data-aw-ed-card]');
      const track = card?.closest('[data-aw-ed-track]');
      if (track) track.dispatchEvent(new CustomEvent('aw:pdp:select',{detail:{index:[...track.querySelectorAll('[data-aw-ed-card]')].indexOf(card)}}));
      const point = event.target.closest?.('[data-aw-ed-hotspot]');
      if (point) point.click();
      const answer = event.target.closest?.('.aw-ed-answer-list details');
      if (answer) answer.open = true;
    });
    instances.set(root,() => {
      controller.abort();
      cleanups.forEach(cleanup => cleanup());
      delete root.dataset.awEditorialReady;
    });
  }
  const initWithin = scope => {
    if (scope.matches?.('[data-aw-editorial]')) init(scope);
    scope.querySelectorAll('[data-aw-editorial]').forEach(init);
    window.AlawooderProduct?.refreshAll();
  };
  window.AlawooderEditorial = {mountAll:initWithin};
  initWithin(document);
  document.addEventListener('shopify:section:load', event => initWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((cleanup,root) => { if (event.target === root || event.target.contains(root)) { cleanup(); instances.delete(root); } });
  });
})();
